// src/api.js
// Central API client for Karvi AI — the single place every page imports from.
//
// Emergent injects the backend URL as REACT_APP_BACKEND_URL at deploy time.
// Every backend route lives under the "/api" prefix (see backend/server.py).
// NEVER hardcode a localhost URL here — always go through the env var.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "karvi_token";
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

// ---- Client/security metadata (sent on register + login) ----
// device_id is an app-generated install id (persisted locally), NOT a hardware
// serial — matches the mobile app's getClientMeta(). IP + user-agent are added
// server-side. All fields are optional on the backend, so this can't break auth.
export const APP_VERSION = "1.0.0";
const DEVICE_ID_KEY = "karvi_device_id";
function getDeviceId() {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}
function clientMeta() {
  return { device_id: getDeviceId(), platform: "web", app_version: APP_VERSION };
}

async function request(path, { method = "GET", body, auth = true } = {}) {
  const headers = { "Content-Type": "application/json" };
  const token = getToken();
  if (auth && token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${API}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  let data = null;
  try {
    data = await res.json();
  } catch (_) {}

  if (!res.ok) {
    const detail = data && data.detail;
    const message =
      typeof detail === "string"
        ? detail
        : detail && detail.message
        ? detail.message
        : `Request failed (${res.status})`;
    const err = new Error(message);
    err.status = res.status;
    err.detail = detail; // e.g. { error: "INSUFFICIENT_CREDITS", credits_required, credits_available }
    throw err;
  }
  return data;
}

const qs = (obj) => {
  const q = new URLSearchParams();
  Object.entries(obj).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "" && v !== "all") q.set(k, v);
  });
  const s = q.toString();
  return s ? `?${s}` : "";
};

// ---------------- Auth ----------------
export const auth = {
  login: (email, password) =>
    request("/auth/login", { method: "POST", auth: false, body: { email, password, ...clientMeta() } }),
  register: (payload) =>
    request("/auth/register", { method: "POST", auth: false, body: { ...payload, ...clientMeta() } }),
  me: () => request("/auth/me"),
  updateSettings: (payload) => request("/settings", { method: "PATCH", body: payload }),
  verifyAge: (date_of_birth) => request("/users/verify-age", { method: "PATCH", body: { date_of_birth } }),
  deleteAccount: () => request("/auth/delete-account", { method: "DELETE" }),
  // password reset (public, 3 steps)
  forgotPassword: (email) => request("/auth/forgot-password", { method: "POST", auth: false, body: { email } }),
  verifyOtp: (email, otp) => request("/auth/verify-otp", { method: "POST", auth: false, body: { email, otp } }),
  resetPassword: (email, new_password) =>
    request("/auth/reset-password", { method: "POST", auth: false, body: { email, new_password } }),
};

// ---------------- Notifications ----------------
export const notifications = {
  list: () => request("/notifications"),
  unreadCount: () => request("/notifications/unread-count"),
  markRead: (id) => request(`/notifications/${id}/read`, { method: "POST" }),
  markAllRead: () => request("/notifications/read-all", { method: "POST" }),
  remove: (id) => request(`/notifications/${id}`, { method: "DELETE" }),
};

// ---------------- Characters (bots) ----------------
export const bots = {
  list: (params = {}) => request(`/bots${qs({ sort_by: "popular", page: 1, limit: 24, ...params })}`),
  categories: () => request("/bots/categories", { auth: false }),
  detail: (id) => request(`/bots/${id}`),
  like: (id) => request(`/bots/${id}/like`, { method: "POST" }),
  create: (payload) => request("/bots", { method: "POST", body: payload }),
  myCreated: () => request("/bots/my-created"),
  remove: (id) => request(`/bots/${id}`, { method: "DELETE" }),
  // AI-assisted persona draft (name, description, story, personality, first_message, image_prompt)
  aiDraft: (prompt, name) => request("/generate-ai-character", { method: "POST", body: { prompt, name } }),
  // Lightweight random sample — used by the landing page's rotating banner.
  random: (limit = 10) => request(`/bots/random?limit=${limit}`, { auth: false }),
};

// ---------------- Chat ----------------
export const chat = {
  history: (botId) => request(`/chat/${botId}`),
  send: (botId, message, opts = {}) =>
    request(`/chat/${botId}`, { method: "POST", body: { message, regenerate: false, memory_hint: "", ...opts } }),
  regenerate: (botId, lastUserMessage = "") =>
    request(`/chat/${botId}`, { method: "POST", body: { message: lastUserMessage, regenerate: true, memory_hint: "" } }),
  deleteMessage: (botId, timestamp) =>
    request(`/chat/${botId}/message`, { method: "DELETE", body: { timestamp } }),
  clear: (botId) => request(`/chat/${botId}`, { method: "DELETE" }),
};

// ---------------- Drift (social feed of remixable media) ----------------
export const drift = {
  feed: (page = 1, limit = 24) => request(`/drift/feed${qs({ page, limit })}`),
  discoverCategories: () => request("/drift/discover/categories"),
  mostRemixed: () => request("/drift/discover/most-remixed"),
  category: (name) => request(`/drift/category/${encodeURIComponent(name)}`),
  post: (id) => request(`/drift/${id}`),
  publicPost: (id) => request(`/drift/public/${id}`, { auth: false }),
  like: (id) => request(`/drift/${id}/like`, { method: "POST" }),
  comments: (id) => request(`/drift/${id}/comments`),
  comment: (id, text) => request(`/drift/${id}/comment`, { method: "POST", body: { text } }),
  view: (id, watch_time = 0) => request(`/drift/${id}/view`, { method: "POST", auth: false, body: { watch_time } }),
  trackShare: (id) => request(`/drift/${id}/share`, { method: "POST", auth: false }),
  share: (payload) => request("/drift/share", { method: "POST", body: payload }), // publish a drop to Drift
  remove: (id) => request(`/drift/${id}`, { method: "DELETE" }), // delete your own post
};

// ---------------- AI Assistant (general chat) ----------------
export const assistant = {
  get: () => request("/assistant/chat"),
  send: (message) => request("/assistant/chat", { method: "POST", body: { message, regenerate: false, memory_hint: "" } }),
  clear: () => request("/assistant/chat", { method: "DELETE" }),
  newChat: () => request("/assistant/new-chat", { method: "POST" }),
  history: () => request("/assistant/history"),
  restore: (historyId) => request(`/assistant/restore-chat/${historyId}`, { method: "POST" }),
};

// ---------------- Model listings (pricing) ----------------
export const models = {
  video: () => request("/models/video", { auth: false }),
  image: () => request("/models/image", { auth: false }),
};

// ---------------- Campaigns (homepage banner + purchase offers) ----------------
export const campaigns = {
  active: () => request("/campaigns/active", { auth: false }),
};

// ---------------- Venice access (NSFW trial / paid unlock) ----------------
export const venice = {
  status: () => request("/venice/status"),
  startTrial: () => request("/venice/start-trial", { method: "POST" }),
  createOrder: () => request("/venice/create-order", { method: "POST" }),
  verify: (payload) => request("/venice/verify-payment", { method: "POST", body: payload }),
};

// ---------------- Multi-shot director (expands a prompt into scenes) ----------------
export const multishot = {
  // Authenticated: the endpoint spends model budget on every call, so it now
  // requires a token. This used to be sent with auth:false, which would 403
  // against the hardened backend. CreateVideo is an auth-gated page, so a
  // token is always present by the time this runs.
  generate: (payload) => request("/ai/multishot", { method: "POST", body: payload }),
};

// ---------------- Conversation memory ----------------
export const memory = {
  get: (botId) => request(`/memory/${botId}`),
  graph: (botId) => request(`/memory/${botId}/graph`),
  muteNode: (botId, nodeId, muted) =>
    request(`/memory/${botId}/graph/${nodeId}/mute`, { method: "PATCH", body: { muted } }),
  remove: (botId, mem) => request("/memory", { method: "DELETE", body: { bot_id: botId, memory: mem } }),
};

// ---------------- Social (follow / creators) ----------------
export const social = {
  follow: (id) => request(`/social/follow/${id}`, { method: "POST" }),
  unfollow: (id) => request(`/social/follow/${id}`, { method: "DELETE" }),
  followStatus: (id) => request(`/social/follow/status/${id}`),
  creator: (id) => request(`/social/creator/${id}`),
  creatorDrifts: (id, page = 1) => request(`/social/creator/${id}/drifts?page=${page}&limit=12`),
  trending: () => request("/social/trending-creators"),
  followers: () => request("/social/followers"),
  following: () => request("/social/following"),
};

// ---------------- Drops (your saved generations) ----------------
export const drops = {
  list: ({ type, page = 1, limit = 24 } = {}) => request(`/drops${qs({ type, page, limit })}`),
  remove: (id) => request(`/drops/${id}`, { method: "DELETE" }),
  removeAll: () => request("/drops/all", { method: "DELETE" }),
  bulkDelete: (drop_ids) => request("/drops/bulk-delete", { method: "POST", body: { drop_ids } }),
};

// ---------------- Free clips (Pexels + Pixabay, proxied server-side) ----------------
// The provider keys stay on the backend — never call the providers from here.
// `importClip` copies one asset into Karvi storage and returns its own URL,
// which is what Recreate then feeds to a studio.
export const freeclips = {
  categories: () => request("/discover/free-clips/categories", { auth: false }),
  list: ({ category = "all", kind = "all", limit = 12 } = {}) =>
    request(`/discover/free-clips${qs({ category, kind, limit })}`, { auth: false }),
  importClip: ({ provider, asset_id, kind }) =>
    request("/discover/free-clips/import", { method: "POST", body: { provider, asset_id, kind } }),
};

// ---------------- Image generation ----------------
export const images = {
  generate: (payload) => request("/images/generate", { method: "POST", body: payload }),
  generateBot: (prompt) => request("/images/generate-bot", { method: "POST", body: { prompt } }), // avatar, no credit cost
  refx: (payload) => request("/images/refx", { method: "POST", body: payload }),
  pixsync: (payload) => request("/images/pixsync", { method: "POST", body: payload }),
  uploadTemp: (image_base64) => request("/images/upload-temp", { method: "POST", body: { image_base64 } }),
};

// ---------------- Credits (Razorpay top-up) ----------------
export const credits = {
  balance: () => request("/credits"),
  packages: () => request("/credits/packages"),
  createOrder: (package_id) => request("/credits/create-order", { method: "POST", body: { package_id } }),
  verify: (payload) => request("/credits/verify-payment", { method: "POST", body: payload }),
  transactions: (limit = 20) => request(`/credits/transactions?limit=${limit}`),
  payments: (limit = 20) => request(`/credits/payments?limit=${limit}`),
};

// ---------------- Subscription plans (Razorpay) ----------------
export const subscription = {
  plans: () => request("/subscription/plans", { auth: false }),
  createOrder: (tier) => request("/subscription/create-order", { method: "POST", body: { tier } }),
  verify: (payload) => request("/subscription/verify-payment", { method: "POST", body: payload }),
  cancel: () => request("/subscription/cancel", { method: "POST" }),
};

// ---------------- Misc ----------------
export const misc = {
  feedback: (message) => request("/feedback", { method: "POST", body: { message } }),
};

// ---------------- Video generation (async — submit then poll) ----------------
export const videos = {
  generate: (payload) => request("/videos/generate", { method: "POST", body: payload }),
  status: (videoId) => request(`/videos/${videoId}`),
  history: () => request("/videos/history"),
  remove: (videoId) => request(`/videos/${videoId}`, { method: "DELETE" }),

  generateSpeech: (text, voice = "wise_woman") =>
    request("/videos/generate-speech", { method: "POST", body: { text, voice } }),

  // Multipart upload — can't go through request() (JSON). Returns { audio_url }.
  async uploadAudio(file) {
    const fd = new FormData();
    fd.append("file", file);
    const token = getToken();
    const res = await fetch(`${API}/videos/upload-audio`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd,
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) throw new Error((data && data.detail) || "Audio upload failed");
    return data;
  },

  // Convenience: submit, then poll until completed/failed. Calls onTick(video) each poll.
  async generateAndWait(payload, { onTick, intervalMs = 4000, timeoutMs = 240000 } = {}) {
    const submit = await this.generate(payload);
    const videoId = submit.video_id || submit.id || (submit.video && submit.video.video_id);
    if (!videoId) return submit; // some backends return the finished video inline
    const started = Date.now();
    // eslint-disable-next-line no-constant-condition
    while (true) {
      await new Promise((r) => setTimeout(r, intervalMs));
      const res = await this.status(videoId);
      const video = res.video || res;
      onTick && onTick(video);
      const status = (video.status || "").toLowerCase();
      if (status === "completed" || status === "failed") return video;
      if (Date.now() - started > timeoutMs) return video;
    }
  },
};
